<template>
  <div class="paper-editor">
    <!-- 주석 목록 (상단) -->
    <div class="footnote-list-top">
      <div class="footnote-list-header">
        <span class="footnote-list-title">주석 목록</span>
        <span class="footnote-count">({{ footnotes.length }}개)</span>
        <button type="button" class="toolbar-btn add-btn" @click="showFootnoteModal = true">+ 추가</button>
      </div>
      <div v-if="footnotes.length > 0" class="footnote-list-content">
        <div v-for="(fn, index) in footnotes" :key="index" class="footnote-item">
          <div class="footnote-item-header">
            <sup class="footnote-number">{{ fn.superscript }})</sup>
            <span class="footnote-page">p.{{ fn.page }}</span>
            <button type="button" class="footnote-delete" @click="deleteFootnote(index)">×</button>
          </div>
          <div class="footnote-text">{{ fn.content }}</div>
        </div>
      </div>
    </div>

    <!-- 툴바 -->
    <div class="editor-toolbar">
      <button type="button" class="toolbar-btn format-btn" @click="insertBold" title="볼드 (**텍스트**)">
        <strong>B</strong>
      </button>
      <button type="button" class="toolbar-btn format-btn" @click="insertItalic" title="이탤릭 (*텍스트*)">
        <em>I</em>
      </button>
      <span class="toolbar-divider"></span>
      <button type="button" class="toolbar-btn format-btn" @click="insertIndent" title="들여쓰기 (&nbsp;)">
        ⇥
      </button>
      <button type="button" class="toolbar-btn format-btn" @click="insertBlockquote" title="인용문 (>)">
        ❝
      </button>
      <button type="button" class="toolbar-btn format-btn" @click="insertLineBreak" title="줄바꿈 (<br>)">
        ↵
      </button>
      <span class="toolbar-divider"></span>
      <button type="button" class="toolbar-btn" @click="insertPageBreak">페이지 나누기</button>
      <button type="button" class="toolbar-btn" @click="openTableModal">표 삽입</button>
      <button type="button" class="toolbar-btn" @click="openFigureModal">그림 삽입</button>
      <button type="button" class="toolbar-btn" @click="showFootnoteModal = true">
        <sup>n</sup> 주석
      </button>
      <button type="button" class="toolbar-btn" @click="showHelp = true">사용법</button>
      <div class="toolbar-spacer"></div>
      <button type="button" class="toolbar-btn primary" @click="previewHtml">미리보기</button>
    </div>

    <!-- 텍스트 입력 영역 -->
    <textarea
      ref="textInput"
      v-model="content"
      class="text-input"
      :style="{ height: height + 'px' }"
      placeholder="여기에 논문 텍스트를 입력하세요.

규칙:
• 1. / 2. 장 제목 (H2)
• 1.1 / 2.1 절 제목 (H3)
• 1.1.1 소절 제목 (H4)
• > 인용문
• <표 1> 표 제목
• <그림 1> 그림 제목
• 참고문헌"
      @input="handleInput"
    ></textarea>

    <!-- 미리보기 모달 -->
    <div v-if="showPreview" class="modal-overlay" @click.self="showPreview = false">
      <div class="modal-content preview-modal">
        <div class="modal-header">
          <h3>미리보기</h3>
          <button type="button" class="close-btn" @click="showPreview = false">×</button>
        </div>
        <div class="modal-body">
          <div class="preview-content" v-html="previewContent"></div>
        </div>
      </div>
    </div>

    <!-- 사용법 모달 -->
    <div v-if="showHelp" class="modal-overlay" @click.self="showHelp = false">
      <div class="modal-content help-modal">
        <div class="modal-header">
          <h3>사용법 안내</h3>
          <button type="button" class="close-btn" @click="showHelp = false">×</button>
        </div>
        <div class="modal-body help-content">
          <h4>텍스트 서식</h4>
          <pre>**볼드 텍스트**  또는  __볼드__
*이탤릭 텍스트* 또는  _이탤릭_
***볼드+이탤릭***</pre>

          <h4>장/절 제목 (자동 변환)</h4>
          <pre>1. 서론          → H2 (장)
1.1 배경        → H3 (절)
1.1.1 세부      → H4 (소절)</pre>

          <h4>인용문</h4>
          <pre>> 인용문 첫째 줄
> 인용문 둘째 줄</pre>

          <h4>표 삽입</h4>
          <pre>&lt;표 1&gt; 표 제목
| 헤더1 | 헤더2 | 헤더3 |
| 내용1 | 내용2 | 내용3 |</pre>

          <h4>그림 삽입</h4>
          <pre>&lt;그림 1&gt; 그림 제목</pre>

          <h4>참고문헌</h4>
          <pre>참고문헌

강현화(2016), 「논문 제목」...</pre>

          <h4>페이지 나누기</h4>
          <pre>---
(대시 3개 이상으로 강제 페이지 분할)</pre>
        </div>
      </div>
    </div>

    <!-- 주석 삽입 모달 -->
    <div v-if="showFootnoteModal" class="modal-overlay" @click.self="showFootnoteModal = false">
      <div class="modal-content footnote-modal">
        <div class="modal-header">
          <h3>주석 삽입</h3>
          <button type="button" class="close-btn" @click="showFootnoteModal = false">×</button>
        </div>
        <div class="modal-body">
          <div class="footnote-form-row">
            <div class="footnote-field">
              <label>첨자 번호</label>
              <input type="text" v-model="footnoteNumber" placeholder="예: 1, 2" />
            </div>
            <div class="footnote-field">
              <label>페이지</label>
              <input type="number" v-model="footnotePage" min="1" placeholder="페이지" />
            </div>
          </div>
          <div class="footnote-field">
            <label>주석 내용</label>
            <textarea v-model="footnoteContent" placeholder="주석 내용을 입력하세요" rows="3"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="toolbar-btn" @click="showFootnoteModal = false">취소</button>
          <button type="button" class="toolbar-btn primary" @click="insertFootnote">삽입</button>
        </div>
      </div>
    </div>

    <!-- 표 삽입 모달 -->
    <div v-if="showTableModal" class="modal-overlay" @click.self="showTableModal = false">
      <div class="modal-content table-modal">
        <div class="modal-header">
          <h3>표 삽입</h3>
          <button type="button" class="close-btn" @click="showTableModal = false">×</button>
        </div>
        <div class="modal-body">
          <p class="table-hint">
            Excel이나 스프레드시트에서 복사한 데이터를 아래에 붙여넣으세요.<br>
            탭(Tab)으로 구분된 데이터가 자동으로 표로 변환됩니다.
          </p>

          <div class="table-form-row">
            <div class="table-field">
              <label>표 번호</label>
              <input type="number" v-model="tableNumber" min="1" />
            </div>
            <div class="table-field" style="flex: 1;">
              <label>표 제목</label>
              <input type="text" v-model="tableCaption" placeholder="표 제목 입력" />
            </div>
          </div>

          <div class="table-field">
            <label>데이터 붙여넣기</label>
            <textarea
              v-model="tableData"
              class="table-data-input"
              placeholder="Excel에서 복사한 데이터를 여기에 붙여넣으세요.

예시 (탭으로 구분):
항목	값1	값2
A	10	20
B	30	40"
              @input="updateTablePreview"
            ></textarea>
          </div>

          <div class="table-field">
            <label>미리보기</label>
            <div class="table-preview" v-html="tablePreviewHtml"></div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="toolbar-btn" @click="showTableModal = false">취소</button>
          <button type="button" class="toolbar-btn primary" @click="insertTable">삽입</button>
        </div>
      </div>
    </div>

    <!-- 그림 삽입 모달 -->
    <div v-if="showFigureModal" class="modal-overlay" @click.self="showFigureModal = false">
      <div class="modal-content figure-modal">
        <div class="modal-header">
          <h3>그림 삽입</h3>
          <button type="button" class="close-btn" @click="showFigureModal = false">×</button>
        </div>
        <div class="modal-body">
          <div class="figure-form-row">
            <div class="figure-field">
              <label>그림 번호</label>
              <input type="number" v-model="figureNumber" min="1" />
            </div>
            <div class="figure-field" style="flex: 1;">
              <label>그림 제목</label>
              <input type="text" v-model="figureCaption" placeholder="그림 제목 입력" />
            </div>
          </div>

          <div class="figure-field">
            <label>이미지 URL</label>
            <input type="text" v-model="figureUrl" placeholder="https://example.com/image.jpg" />
          </div>

          <div class="figure-field">
            <label>이미지 파일 업로드</label>
            <input type="file" accept="image/*" @change="handleFigureFileSelect" ref="figureFileInput" />
          </div>

          <div v-if="figurePreviewUrl" class="figure-preview">
            <label>미리보기</label>
            <div class="figure-preview-img">
              <img :src="figurePreviewUrl" alt="미리보기" />
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="toolbar-btn" @click="showFigureModal = false" :disabled="isUploading">취소</button>
          <button type="button" class="toolbar-btn primary" @click="insertFigure" :disabled="isUploading">
            {{ isUploading ? '업로드 중...' : '삽입' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-console */
import { fileApi } from "@/api/api";

const SERVER_URL = process.env.VUE_APP_SERVER_URI;

export default {
  name: "PaperEditor",
  props: {
    value: {
      type: String,
      default: "",
    },
    height: {
      type: Number,
      default: 700,
    },
    comments: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      content: "",
      footnotes: [],
      showPreview: false,
      showHelp: false,
      showFootnoteModal: false,
      showTableModal: false,
      previewContent: "",
      footnoteNumber: "",
      footnotePage: 1,
      footnoteContent: "",
      // 표 삽입
      tableNumber: 1,
      tableCaption: "",
      tableData: "",
      tablePreviewHtml: '<span class="placeholder">데이터를 입력하면 미리보기가 표시됩니다.</span>',
      // 그림 삽입
      showFigureModal: false,
      figureNumber: 1,
      figureCaption: "",
      figureUrl: "",
      figurePreviewUrl: "",
      figureFile: null,
      isUploading: false,
      // 내부 편집 중 플래그 (watch 무시용)
      isInternalEdit: false,
    };
  },
  created() {
    // 초기값이 HTML인 경우 텍스트로 변환
    this.content = this.htmlToText(this.value || "");
  },
  methods: {
    handleInput() {
      // 내부 편집 플래그 설정 (watch에서 무시하도록)
      this.isInternalEdit = true;
      // 텍스트를 HTML로 변환하여 emit
      const html = this.textToHtml(this.content);
      this.$emit("input", html);
      this.$emit("update-content", html);
      // 다음 틱에서 플래그 해제
      this.$nextTick(() => {
        this.isInternalEdit = false;
      });
    },

    // HTML을 텍스트로 변환
    htmlToText(html) {
      if (!html || html.trim() === "") return "";

      // 이미 plain text인 경우 그대로 반환
      if (!html.includes("<") && !html.includes(">")) {
        return html;
      }

      // 먼저 주석 마커를 텍스트 형식으로 복원
      // <sup data-footnote-id="N">N)</sup> -> ^N)
      let processedHtml = html.replace(/<sup[^>]*data-footnote-id="(\d+)"[^>]*>\d+\)<\/sup>/gi, '^$1)');

      // 볼드/이탤릭 태그를 마크다운으로 복원
      // 볼드+이탤릭: <strong><em>텍스트</em></strong> -> ***텍스트***
      processedHtml = processedHtml.replace(/<strong><em>(.+?)<\/em><\/strong>/gi, '***$1***');
      processedHtml = processedHtml.replace(/<em><strong>(.+?)<\/strong><\/em>/gi, '***$1***');
      // 볼드: <strong>텍스트</strong> -> **텍스트**
      processedHtml = processedHtml.replace(/<strong>(.+?)<\/strong>/gi, '**$1**');
      // 이탤릭: <em>텍스트</em> -> *텍스트*
      processedHtml = processedHtml.replace(/<em>(.+?)<\/em>/gi, '*$1*');

      const temp = document.createElement("div");
      temp.innerHTML = processedHtml;

      const lines = [];
      let lastWasBlock = false; // 마지막 요소가 블록 요소였는지 추적 (빈 줄 삽입용)

      const processNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          // 들여쓰기(nbsp) 보존: \u00A0를 &nbsp;로 변환, 뒷부분만 trim
          const text = node.textContent.replace(/\u00A0/g, '&nbsp;').replace(/\s+$/, '');
          if (text.replace(/\s/g, '').replace(/&nbsp;/g, '')) return text; // 공백만 있는 경우 제외
          return "";
        }

        if (node.nodeType !== Node.ELEMENT_NODE) return "";

        const tag = node.tagName.toLowerCase();

        // 제목 처리
        if (tag.match(/^h[2-6]$/)) {
          // 이전 블록 요소가 있었으면 빈 줄 추가
          if (lastWasBlock && lines.length > 0 && lines[lines.length - 1] !== "") {
            lines.push("");
          }
          // innerHTML에서 &nbsp; 보존
          const text = node.innerHTML
            .replace(/<[^>]+>/gi, '') // 모든 태그 제거
            .replace(/\s+$/, '');
          lines.push(text);
          lastWasBlock = true;
          return "";
        }

        // 단락 처리
        if (tag === "p") {
          // 빈 줄 태그 처리
          if (node.classList.contains("empty-line")) {
            lines.push("");
            lastWasBlock = true;
            return "";
          }
          // 이전 블록 요소가 있었으면 빈 줄 추가 (문단 구분)
          if (lastWasBlock && lines.length > 0 && lines[lines.length - 1] !== "") {
            lines.push("");
          }
          // innerHTML에서 직접 텍스트 추출 (nbsp, br 보존)
          const preservedText = node.innerHTML
            .replace(/<sup[^>]*data-footnote-id="(\d+)"[^>]*>\d+\)<\/sup>/gi, '^$1)')
            .replace(/<strong><em>(.+?)<\/em><\/strong>/gi, '***$1***')
            .replace(/<em><strong>(.+?)<\/strong><\/em>/gi, '***$1***')
            .replace(/<strong>(.+?)<\/strong>/gi, '**$1**')
            .replace(/<em>(.+?)<\/em>/gi, '*$1*')
            .replace(/<(?!br)[^>]+>/gi, '') // br 제외 모든 태그 제거
            .replace(/\s+$/, ''); // 뒷부분만 trim
          if (preservedText) {
            lines.push(preservedText);
            lastWasBlock = true;
          }
          return "";
        }

        // 인용문 처리
        if (tag === "blockquote" || node.classList.contains("blockquote")) {
          // 이전 블록 요소가 있었으면 빈 줄 추가
          if (lastWasBlock && lines.length > 0 && lines[lines.length - 1] !== "") {
            lines.push("");
          }
          // innerHTML에서 직접 추출하여 &nbsp; 보존
          const text = node.innerHTML
            .replace(/<br\s*\/?>/gi, '\n') // <br>을 줄바꿈으로
            .replace(/<[^>]+>/gi, '') // 모든 태그 제거
            .replace(/\s+$/, '');
          text.split("\n").forEach(line => {
            const trimmedLine = line.replace(/\s+$/, '');
            if (trimmedLine.replace(/\s/g, '').replace(/&nbsp;/g, '')) lines.push("> " + trimmedLine);
          });
          lastWasBlock = true;
          return "";
        }

        // 표 처리
        if (tag === "table") {
          // 이전 블록 요소가 있었으면 빈 줄 추가
          if (lastWasBlock && lines.length > 0 && lines[lines.length - 1] !== "") {
            lines.push("");
          }
          // 스타일이 있는 표는 HTML 그대로 보존
          const hasStyle = node.hasAttribute("style") ||
            node.querySelector("[style]") !== null ||
            node.hasAttribute("width") ||
            node.querySelector("[width]") !== null;

          if (hasStyle) {
            // 스타일 있는 표는 HTML 마커로 감싸서 보존
            lines.push("<!--TABLE_HTML_START-->");
            lines.push(node.outerHTML);
            lines.push("<!--TABLE_HTML_END-->");
            lastWasBlock = true;
            return "";
          }

          // 스타일 없는 기본 표는 텍스트로 변환
          const caption = node.querySelector("caption");
          if (caption) {
            // innerHTML에서 &nbsp; 보존
            const captionText = caption.innerHTML.replace(/<[^>]+>/gi, '').replace(/\s+$/, '');
            // <표 1> 형식 추출
            const match = captionText.match(/<표\s*(\d+)>\s*(.*)/);
            if (match) {
              lines.push(`<표 ${match[1]}> ${match[2]}`);
            } else {
              lines.push(captionText);
            }
          }

          const rows = node.querySelectorAll("tr");
          rows.forEach(row => {
            const cells = row.querySelectorAll("th, td");
            // innerHTML에서 &nbsp; 보존
            const cellTexts = Array.from(cells).map(c => c.innerHTML.replace(/<[^>]+>/gi, '').replace(/\s+$/, ''));
            lines.push("| " + cellTexts.join(" | ") + " |");
          });
          lastWasBlock = true;
          return "";
        }

        // 그림 처리 (이미지가 있는 경우 HTML 보존)
        if (node.classList.contains("figure-container")) {
          // 이전 블록 요소가 있었으면 빈 줄 추가
          if (lastWasBlock && lines.length > 0 && lines[lines.length - 1] !== "") {
            lines.push("");
          }
          const img = node.querySelector("img");
          if (img && img.src) {
            // 이미지가 있으면 HTML 마커로 감싸서 보존
            lines.push("<!--FIGURE_HTML_START-->");
            lines.push(node.outerHTML);
            lines.push("<!--FIGURE_HTML_END-->");
            lastWasBlock = true;
            return "";
          }
          // 이미지 없이 캡션만 있는 경우
          const caption = node.querySelector(".figure-caption");
          if (caption) {
            // innerHTML에서 &nbsp; 보존
            lines.push(caption.innerHTML.replace(/<[^>]+>/gi, '').replace(/\s+$/, ''));
          }
          lastWasBlock = true;
          return "";
        }

        // 페이지 나누기 처리
        if (node.classList.contains("page-break")) {
          // 이전 블록 요소가 있었으면 빈 줄 추가
          if (lastWasBlock && lines.length > 0 && lines[lines.length - 1] !== "") {
            lines.push("");
          }
          lines.push("---");
          lastWasBlock = true;
          return "";
        }

        // 참고문헌 제목 처리
        if (node.classList.contains("references-title")) {
          // 이전 블록 요소가 있었으면 빈 줄 추가
          if (lastWasBlock && lines.length > 0 && lines[lines.length - 1] !== "") {
            lines.push("");
          }
          lines.push("참고문헌");
          lastWasBlock = true;
          return "";
        }

        // 참고문헌 항목 처리
        if (node.classList.contains("reference-item")) {
          // 참고문헌 항목 간에는 빈 줄 없이 연속, innerHTML에서 &nbsp; 보존
          lines.push(node.innerHTML.replace(/<[^>]+>/gi, '').replace(/\s+$/, ''));
          lastWasBlock = true;
          return "";
        }

        // 참고문헌 섹션 래퍼 처리 (하위 호환성)
        if (node.classList.contains("references-section")) {
          // 이전 블록 요소가 있었으면 빈 줄 추가
          if (lastWasBlock && lines.length > 0 && lines[lines.length - 1] !== "") {
            lines.push("");
          }
          lines.push("참고문헌");
          const items = node.querySelectorAll(".reference-item");
          items.forEach(item => {
            // innerHTML에서 &nbsp; 보존
            lines.push(item.innerHTML.replace(/<[^>]+>/gi, '').replace(/\s+$/, ''));
          });
          lastWasBlock = true;
          return "";
        }

        // 리스트 처리
        if (tag === "ul" || tag === "ol") {
          // 이전 블록 요소가 있었으면 빈 줄 추가
          if (lastWasBlock && lines.length > 0 && lines[lines.length - 1] !== "") {
            lines.push("");
          }
          const items = node.querySelectorAll("li");
          items.forEach((li, idx) => {
            const prefix = tag === "ol" ? `${idx + 1}. ` : "• ";
            // innerHTML에서 &nbsp; 보존
            lines.push(prefix + li.innerHTML.replace(/<[^>]+>/gi, '').replace(/\s+$/, ''));
          });
          lastWasBlock = true;
          return "";
        }

        // 자식 노드 처리
        Array.from(node.childNodes).forEach(child => processNode(child));
        return "";
      };

      Array.from(temp.childNodes).forEach(child => processNode(child));

      // 앞뒤 빈 줄만 제거 (내부 빈 줄은 유지)
      let result = lines.join("\n");
      // 앞부분 빈 줄 제거
      result = result.replace(/^\n+/, '');
      // 뒷부분 빈 줄 제거
      result = result.replace(/\n+$/, '');
      return result;
    },

    insertFootnote() {
      if (!this.footnoteNumber || !this.footnoteContent) {
        alert("첨자 번호와 주석 내용을 입력해주세요.");
        return;
      }

      const newFootnote = {
        idx: Date.now(),
        superscript: this.footnoteNumber,
        page: parseInt(this.footnotePage) || 1,
        content: this.footnoteContent,
      };

      this.footnotes.push(newFootnote);
      this.$emit("update:comments", this.footnotes);

      const textarea = this.$refs.textInput;
      const cursorPos = textarea.selectionStart;
      const marker = `^${this.footnoteNumber})`;  // ^ 추가하여 위첨자 마커로 변환
      this.content = this.content.substring(0, cursorPos) + marker + this.content.substring(cursorPos);

      this.showFootnoteModal = false;
      this.footnoteNumber = "";
      this.footnoteContent = "";

      this.handleInput();
    },

    deleteFootnote(index) {
      this.footnotes.splice(index, 1);
      this.$emit("update:comments", this.footnotes);
    },

    // 볼드 서식 삽입/토글
    insertBold() {
      const textarea = this.$refs.textInput;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = this.content.substring(start, end);

      if (selectedText) {
        // 선택된 텍스트가 있으면 **로 감싸기 (또는 제거)
        if (selectedText.startsWith('**') && selectedText.endsWith('**')) {
          // 볼드 제거
          const unformatted = selectedText.slice(2, -2);
          this.content = this.content.substring(0, start) + unformatted + this.content.substring(end);
          this.$nextTick(() => {
            textarea.setSelectionRange(start, start + unformatted.length);
            textarea.focus();
          });
        } else {
          // 볼드 추가
          const formatted = `**${selectedText}**`;
          this.content = this.content.substring(0, start) + formatted + this.content.substring(end);
          this.$nextTick(() => {
            textarea.setSelectionRange(start, start + formatted.length);
            textarea.focus();
          });
        }
      } else {
        // 선택된 텍스트가 없으면 **커서** 삽입
        this.content = this.content.substring(0, start) + '****' + this.content.substring(end);
        this.$nextTick(() => {
          textarea.setSelectionRange(start + 2, start + 2);
          textarea.focus();
        });
      }
      this.handleInput();
    },

    // 이탤릭 서식 삽입/토글
    insertItalic() {
      const textarea = this.$refs.textInput;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = this.content.substring(start, end);

      if (selectedText) {
        // 선택된 텍스트가 있으면 *로 감싸기 (또는 제거)
        if (selectedText.startsWith('*') && selectedText.endsWith('*') && !selectedText.startsWith('**')) {
          // 이탤릭 제거
          const unformatted = selectedText.slice(1, -1);
          this.content = this.content.substring(0, start) + unformatted + this.content.substring(end);
          this.$nextTick(() => {
            textarea.setSelectionRange(start, start + unformatted.length);
            textarea.focus();
          });
        } else {
          // 이탤릭 추가
          const formatted = `*${selectedText}*`;
          this.content = this.content.substring(0, start) + formatted + this.content.substring(end);
          this.$nextTick(() => {
            textarea.setSelectionRange(start, start + formatted.length);
            textarea.focus();
          });
        }
      } else {
        // 선택된 텍스트가 없으면 *커서* 삽입
        this.content = this.content.substring(0, start) + '**' + this.content.substring(end);
        this.$nextTick(() => {
          textarea.setSelectionRange(start + 1, start + 1);
          textarea.focus();
        });
      }
      this.handleInput();
    },

    // 들여쓰기 삽입 (&nbsp;)
    insertIndent() {
      const textarea = this.$refs.textInput;
      const cursorPos = textarea.selectionStart;
      // &nbsp; 2개 = 약 1em 들여쓰기 (리터럴 텍스트로 삽입)
      const indent = '&nbsp;&nbsp;';
      this.content = this.content.substring(0, cursorPos) + indent + this.content.substring(cursorPos);
      this.$nextTick(() => {
        textarea.setSelectionRange(cursorPos + indent.length, cursorPos + indent.length);
        textarea.focus();
      });
      this.handleInput();
    },

    // 인용문 삽입
    insertBlockquote() {
      const textarea = this.$refs.textInput;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = this.content.substring(start, end);

      if (selectedText) {
        // 선택된 텍스트가 있으면 각 줄 앞에 > 추가
        const quotedText = selectedText.split('\n').map(line => '> ' + line).join('\n');
        this.content = this.content.substring(0, start) + quotedText + this.content.substring(end);
        this.$nextTick(() => {
          textarea.setSelectionRange(start, start + quotedText.length);
          textarea.focus();
        });
      } else {
        // 선택된 텍스트가 없으면 > 삽입
        const before = this.content.substring(0, start);
        const needNewline = before.length > 0 && !before.endsWith('\n');
        const quote = (needNewline ? '\n' : '') + '> ';
        this.content = this.content.substring(0, start) + quote + this.content.substring(end);
        this.$nextTick(() => {
          const newPos = start + quote.length;
          textarea.setSelectionRange(newPos, newPos);
          textarea.focus();
        });
      }
      this.handleInput();
    },

    // 줄바꿈 삽입 (<br> - 인용문/테이블 내 꾸미기용)
    insertLineBreak() {
      const textarea = this.$refs.textInput;
      const cursorPos = textarea.selectionStart;
      const br = '<br>';
      this.content = this.content.substring(0, cursorPos) + br + this.content.substring(cursorPos);
      this.$nextTick(() => {
        textarea.setSelectionRange(cursorPos + br.length, cursorPos + br.length);
        textarea.focus();
      });
      this.handleInput();
    },

    // 페이지 나누기 삽입
    insertPageBreak() {
      const textarea = this.$refs.textInput;
      const cursorPos = textarea.selectionStart;
      const before = this.content.substring(0, cursorPos);
      const after = this.content.substring(cursorPos);

      const needBefore = before.length > 0 && !before.endsWith('\n');
      const needAfter = after.length > 0 && !after.startsWith('\n');

      this.content = before + (needBefore ? '\n' : '') + '---\n' + (needAfter ? '' : '') + after;

      this.handleInput();

      // 커서 위치 복원
      this.$nextTick(() => {
        const newPos = cursorPos + (needBefore ? 1 : 0) + 4;
        textarea.setSelectionRange(newPos, newPos);
        textarea.focus();
      });
    },

    // 표 모달 열기
    openTableModal() {
      // 현재 텍스트에서 마지막 표 번호 찾기
      const existingTables = this.content.match(/<표\s*(\d+)>/g);
      if (existingTables) {
        const lastNum = Math.max(...existingTables.map(t => parseInt(t.match(/\d+/)[0])));
        this.tableNumber = lastNum + 1;
      } else {
        this.tableNumber = 1;
      }
      this.tableCaption = "";
      this.tableData = "";
      this.tablePreviewHtml = '<span class="placeholder">데이터를 입력하면 미리보기가 표시됩니다.</span>';
      this.showTableModal = true;
    },

    // 표 데이터 파싱 (탭, 파이프, 쉼표 구분자 지원)
    parseTableData(text) {
      const lines = text.trim().split(/\r?\n/).filter(line => line.trim());
      if (lines.length === 0) return null;

      // 구분자 감지 (탭 우선, 그 다음 파이프, 마지막으로 쉼표)
      let delimiter = '\t';
      const firstLine = lines[0];
      if (firstLine.includes('\t')) {
        delimiter = '\t';
      } else if (firstLine.includes('|')) {
        delimiter = '|';
      } else if (firstLine.includes(',')) {
        delimiter = ',';
      }

      const rows = lines.map(line => {
        let cells = line.split(delimiter).map(cell => cell.trim());
        // 파이프 구분자의 경우 빈 셀 제거
        if (delimiter === '|') {
          cells = cells.filter(cell => cell !== '');
        }
        return cells;
      });

      // 최대 열 수 확인
      const maxCols = Math.max(...rows.map(r => r.length));
      if (maxCols === 0) return null;

      // 열 수 정규화
      return rows.map(row => {
        while (row.length < maxCols) row.push('');
        return row;
      });
    },

    // 표 미리보기 업데이트
    updateTablePreview() {
      const rows = this.parseTableData(this.tableData);
      if (rows && rows.length > 0) {
        let html = '<table class="preview-table"><thead><tr>';
        rows[0].forEach(cell => {
          html += `<th>${this.escapeHtml(cell)}</th>`;
        });
        html += '</tr></thead>';

        if (rows.length > 1) {
          html += '<tbody>';
          for (let i = 1; i < rows.length; i++) {
            html += '<tr>';
            rows[i].forEach(cell => {
              html += `<td>${this.escapeHtml(cell)}</td>`;
            });
            html += '</tr>';
          }
          html += '</tbody>';
        }
        html += '</table>';
        this.tablePreviewHtml = html;
      } else {
        this.tablePreviewHtml = '<span class="placeholder">데이터를 입력하면 미리보기가 표시됩니다.</span>';
      }
    },

    // 표 삽입
    insertTable() {
      const rows = this.parseTableData(this.tableData);
      if (!rows || rows.length === 0) {
        alert("표 데이터를 입력해주세요.");
        return;
      }

      // 텍스트 형식으로 표 생성
      let tableText = `<표 ${this.tableNumber}> ${this.tableCaption}\n`;
      rows.forEach(row => {
        tableText += '| ' + row.join(' | ') + ' |\n';
      });

      const textarea = this.$refs.textInput;
      const cursorPos = textarea.selectionStart;
      const before = this.content.substring(0, cursorPos);
      const after = this.content.substring(cursorPos);

      const needBefore = before.length > 0 && !before.endsWith('\n');
      const needAfter = after.length > 0 && !after.startsWith('\n');

      this.content = before + (needBefore ? '\n\n' : '') + tableText + (needAfter ? '\n' : '') + after;

      this.showTableModal = false;
      this.tableData = "";
      this.tableCaption = "";
      this.tableNumber++;
      this.tablePreviewHtml = '<span class="placeholder">데이터를 입력하면 미리보기가 표시됩니다.</span>';

      this.handleInput();
    },

    // 그림 모달 열기
    openFigureModal() {
      // 현재 텍스트에서 마지막 그림 번호 찾기
      const existingFigures = this.content.match(/<그림\s*(\d+)>/g);
      if (existingFigures) {
        const lastNum = Math.max(...existingFigures.map(f => parseInt(f.match(/\d+/)[0])));
        this.figureNumber = lastNum + 1;
      } else {
        this.figureNumber = 1;
      }
      this.figureCaption = "";
      this.figureUrl = "";
      this.figurePreviewUrl = "";
      this.figureFile = null;
      if (this.$refs.figureFileInput) {
        this.$refs.figureFileInput.value = "";
      }
      this.showFigureModal = true;
    },

    // 파일 선택 핸들러
    handleFigureFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.figureFile = file;
        // 미리보기용 Data URL 생성
        const reader = new FileReader();
        reader.onload = (e) => {
          this.figurePreviewUrl = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },

    // 그림 삽입
    async insertFigure() {
      let imageUrl = this.figureUrl;

      // 파일이 선택된 경우 백엔드에 업로드
      if (this.figureFile) {
        try {
          this.isUploading = true;

          const formData = new FormData();
          formData.append("file", this.figureFile);
          formData.append("category", "journal");

          const response = await fileApi.uploadFile(formData);
          if (response.status === 200 && response.data.path) {
            imageUrl = response.data.path;
          } else {
            alert("이미지 업로드에 실패했습니다.");
            return;
          }
        } catch (error) {
          console.error("이미지 업로드 오류:", error);
          alert("이미지 업로드 중 오류가 발생했습니다.");
          return;
        } finally {
          this.isUploading = false;
        }
      }

      if (!imageUrl) {
        alert("이미지 URL을 입력하거나 파일을 선택해주세요.");
        return;
      }

      // 캡션 생성 (항상 <그림 N> 표시, 제목은 선택)
      const captionText = this.figureCaption && this.figureCaption.trim()
        ? `&lt;그림 ${this.figureNumber}&gt; ${this.escapeHtml(this.figureCaption)}`
        : `&lt;그림 ${this.figureNumber}&gt;`;

      // HTML로 그림 삽입 (이미지는 테두리 안, 캡션은 테두리 바깥)
      const figureHtml = `<!--FIGURE_HTML_START-->
<div class="figure-container">
  <div class="figure-image-wrapper">
    <img src="${imageUrl}" alt="${this.figureCaption || ''}" />
  </div>
  <div class="figure-caption">${captionText}</div>
</div>
<!--FIGURE_HTML_END-->`;

      const textarea = this.$refs.textInput;
      const cursorPos = textarea.selectionStart;
      const before = this.content.substring(0, cursorPos);
      const after = this.content.substring(cursorPos);

      const needBefore = before.length > 0 && !before.endsWith('\n');
      const needAfter = after.length > 0 && !after.startsWith('\n');

      this.content = before + (needBefore ? '\n\n' : '') + figureHtml + (needAfter ? '\n' : '') + after;

      this.showFigureModal = false;
      this.figureCaption = "";
      this.figureUrl = "";
      this.figurePreviewUrl = "";
      this.figureFile = null;
      this.figureNumber++;

      this.handleInput();
    },

    escapeHtml(s) {
      // &nbsp;를 임시로 보존
      const withNbspPlaceholder = s.replace(/&nbsp;/g, '{{NBSP_TAG}}');
      return withNbspPlaceholder
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;")
        .replace(/  /g, " &nbsp;") // 연속 일반 공백 유지
        .replace(/\{\{NBSP_TAG\}\}/g, "&nbsp;"); // &nbsp; 복원
    },

    // HTML escape하되 <br> 태그는 보존
    escapeHtmlPreserveBr(s) {
      // <br> 태그 임시 보존
      const withPlaceholder = s.replace(/<br\s*\/?>/gi, '{{BR_TAG}}');
      const escaped = this.escapeHtml(withPlaceholder);
      // <br> 복원
      return escaped.replace(/\{\{BR_TAG\}\}/g, '<br>');
    },

    // 인라인 서식 변환 (볼드, 이탤릭, 볼드+이탤릭)
    convertInlineFormatting(text) {
      return text
        // 볼드+이탤릭: ***텍스트*** 또는 ___텍스트___
        .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
        .replace(/___(.+?)___/g, '<strong><em>$1</em></strong>')
        // 볼드: **텍스트** 또는 __텍스트__
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/__(.+?)__/g, '<strong>$1</strong>')
        // 이탤릭: *텍스트* 또는 _텍스트_
        .replace(/\*([^*]+)\*/g, '<em>$1</em>')
        .replace(/_([^_]+)_/g, '<em>$1</em>');
    },

    textToHtml(text) {
      const lines = text.replace(/\r\n?/g, "\n").split("\n");
      const html = [];
      let inReferences = false;
      let paragraphBuf = [];
      let inBlockquote = false;
      let blockquoteBuf = [];
      let emptyLineCount = 0;

      // 주석 마커 ^N) 를 위첨자로 변환 (data-footnote-id 속성 포함)
      const convertFootnoteMarkers = (text) => {
        return text.replace(/\^(\d+)\)/g, '<sup data-footnote-id="$1">$1)</sup>');
      };

      const flushParagraph = () => {
        if (paragraphBuf.length === 0) return;
        // 들여쓰기(nbsp)를 보존하면서 뒷부분만 trim
        const pText = paragraphBuf.join(" ").replace(/\s+$/, '');
        if (pText) {
          // <br> 태그는 임시로 보존 후 복원
          const withBrPlaceholder = pText.replace(/<br\s*\/?>/gi, '{{BR_TAG}}');
          const escaped = this.escapeHtml(withBrPlaceholder);
          const withBrRestored = escaped.replace(/\{\{BR_TAG\}\}/g, '<br>');
          const withFootnotes = convertFootnoteMarkers(withBrRestored);
          const withFormatting = this.convertInlineFormatting(withFootnotes);
          html.push(`<p>${withFormatting}</p>`);
        }
        paragraphBuf = [];
      };

      const flushBlockquote = () => {
        if (blockquoteBuf.length === 0) return;
        const qText = blockquoteBuf.map(t => {
          const withFootnotes = convertFootnoteMarkers(t);
          return this.convertInlineFormatting(withFootnotes);
        }).join("<br>");
        html.push(`<div class="blockquote">${qText}</div>`);
        blockquoteBuf = [];
        inBlockquote = false;
      };

      const parseHeading = (line) => {
        const trimmed = line.trim();
        const m = trimmed.match(/^(\d+(?:\.\d+)*\.?)\s+(.+)$/);
        if (!m) return null;

        const numbering = m[1];
        const title = m[2].trim();
        if (title.length === 0 || title.length > 100) return null;

        const sentenceEnd = /(\.|다\.?|니다\.?|요\.?|한다\.?|하였다\.?|\?|!|;)$/;
        if (sentenceEnd.test(title)) return null;

        const depth = numbering.replace(/\.$/, "").split(".").length;
        let level = 2;
        if (depth === 2) level = 3;
        else if (depth === 3) level = 4;
        else if (depth >= 4) level = 5;

        return { level, numbering, title };
      };

      const parseTableRows = (startIdx) => {
        const rows = [];
        let i = startIdx;

        while (i < lines.length) {
          const line = lines[i];
          const trimmed = line.trim();
          if (trimmed === "") break;
          if (trimmed.match(/^<(표|그림)\s*\d+>/) || trimmed.match(/^(\d+(?:\.\d+)*\.?)\s+.+$/)) break;

          let cells = [];
          if (trimmed.includes("|")) {
            cells = trimmed.split("|").map(c => c.trim()).filter(c => c !== "");
          } else if (line.includes("\t")) {
            cells = line.split("\t").map(c => c.trim()).filter(c => c !== "");
          }

          if (cells.length > 0) rows.push(cells);
          i++;
        }
        return { rows, endIdx: i };
      };

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmed = line.trim();

        // HTML 표 마커 처리 (스타일 있는 표 보존)
        if (trimmed === "<!--TABLE_HTML_START-->") {
          flushParagraph();
          flushBlockquote();
          // TABLE_HTML_END까지 HTML 수집
          let tableHtml = [];
          i++;
          while (i < lines.length && lines[i].trim() !== "<!--TABLE_HTML_END-->") {
            tableHtml.push(lines[i]);
            i++;
          }
          html.push(tableHtml.join("\n"));
          continue;
        }

        // HTML 그림 마커 처리 (이미지 포함 그림 보존)
        if (trimmed === "<!--FIGURE_HTML_START-->") {
          flushParagraph();
          flushBlockquote();
          // FIGURE_HTML_END까지 HTML 수집
          let figureHtml = [];
          i++;
          while (i < lines.length && lines[i].trim() !== "<!--FIGURE_HTML_END-->") {
            figureHtml.push(lines[i]);
            i++;
          }
          html.push(figureHtml.join("\n"));
          continue;
        }

        if (trimmed === "") {
          flushParagraph();
          flushBlockquote();
          emptyLineCount++;
          // 연속된 빈 줄은 빈 단락으로 변환 (2번째부터)
          if (emptyLineCount > 1) {
            html.push('<p class="empty-line">&nbsp;</p>');
          }
          continue;
        }

        // 빈 줄이 아닌 경우 카운터 리셋
        emptyLineCount = 0;

        // 페이지 나누기 (---)
        if (/^-{3,}$/.test(trimmed)) {
          flushParagraph();
          flushBlockquote();
          html.push('<div class="page-break"></div>');
          continue;
        }

        // 참고문헌 제목 감지 (다양한 형식 지원)
        // "참고문헌", "References", "• 참고문헌", "참고 문헌" 등
        const isReferencesTitle = (
          trimmed === "참고문헌" ||
          trimmed === "참고 문헌" ||
          trimmed === "References" ||
          trimmed === "• 참고문헌" ||
          trimmed === "•참고문헌" ||
          /^참고\s*문헌\s*$/i.test(trimmed) ||
          /^references\s*$/i.test(trimmed)
        );
        if (isReferencesTitle) {
          console.log('[PaperEditor] Found references title:', trimmed);
          flushParagraph();
          flushBlockquote();
          inReferences = true;
          // 래퍼 없이 제목만 출력 (페이지 넘김 허용)
          html.push('<div class="references-title">참고문헌</div>');
          continue;
        }

        // 참고문헌 항목 (래퍼 없이 개별 출력)
        if (inReferences) {
          const refText = convertFootnoteMarkers(this.escapeHtml(trimmed));
          html.push(`<p class="reference-item">${refText}</p>`);
          continue;
        }

        if (trimmed.startsWith(">")) {
          flushParagraph();
          inBlockquote = true;
          blockquoteBuf.push(this.escapeHtmlPreserveBr(trimmed.substring(1).trim()));
          continue;
        } else if (inBlockquote) {
          flushBlockquote();
        }

        // 표 - 다음 줄이 |로 시작하는 테이블 행일 때만 표로 인식
        const tableMatch = trimmed.match(/^<표\s*(\d+)>\s*(.*)$/);
        const nextLine = lines[i + 1]?.trim();
        if (tableMatch && nextLine && nextLine.startsWith('|')) {
          flushParagraph();
          const tableNum = tableMatch[1];
          const tableCaption = tableMatch[2] || "";
          const { rows, endIdx } = parseTableRows(i + 1);
          i = endIdx - 1;

          html.push('<div class="table-container">');
          html.push('<table class="table-basic">');
          html.push(`<caption>&lt;표 ${tableNum}&gt; ${convertFootnoteMarkers(this.escapeHtml(tableCaption))}</caption>`);

          if (rows.length > 0) {
            const maxCols = Math.max(...rows.map(r => r.length));
            html.push("<thead><tr>");
            for (let c = 0; c < maxCols; c++) {
              html.push(`<th>${convertFootnoteMarkers(this.escapeHtmlPreserveBr(rows[0][c] || ""))}</th>`);
            }
            html.push("</tr></thead>");

            if (rows.length > 1) {
              html.push("<tbody>");
              for (let r = 1; r < rows.length; r++) {
                html.push("<tr>");
                for (let c = 0; c < maxCols; c++) {
                  html.push(`<td>${convertFootnoteMarkers(this.escapeHtmlPreserveBr(rows[r][c] || ""))}</td>`);
                }
                html.push("</tr>");
              }
              html.push("</tbody>");
            }
          }
          html.push("</table></div>");
          continue;
        }

        const figureMatch = trimmed.match(/^<그림\s*(\d+)>\s*(.*)$/);
        if (figureMatch) {
          flushParagraph();
          html.push('<div class="figure-container">');
          html.push('<div class="figure-placeholder">[그림 영역]</div>');
          html.push(`<div class="figure-caption">&lt;그림 ${figureMatch[1]}&gt; ${convertFootnoteMarkers(this.escapeHtml(figureMatch[2] || ""))}</div>`);
          html.push('</div>');
          continue;
        }

        const heading = parseHeading(trimmed);
        if (heading) {
          flushParagraph();
          const titleFormatted = this.convertInlineFormatting(this.escapeHtml(heading.title));
          const headingText = convertFootnoteMarkers(this.escapeHtml(heading.numbering) + ' ' + titleFormatted);
          html.push(`<h${heading.level}>${headingText}</h${heading.level}>`);
          continue;
        }

        // 들여쓰기(nbsp) 보존: 앞쪽 nbsp는 유지하고 뒷부분만 trim
        const preservedLine = line.replace(/\s+$/, '');
        paragraphBuf.push(preservedLine);
      }

      flushParagraph();
      flushBlockquote();
      // 래퍼 제거로 닫는 태그 불필요

      return html.join("\n");
    },

    // 상대 경로 이미지를 전체 URL로 변환
    processImageUrls(htmlString) {
      if (!htmlString) return htmlString;
      return htmlString.replace(
        /src="(iskal\/[^"]+)"/g,
        `src="${SERVER_URL}/storage/$1"`
      );
    },

    previewHtml() {
      let htmlContent = this.textToHtml(this.content);
      // 이미지 URL 처리
      htmlContent = this.processImageUrls(htmlContent);

      if (this.footnotes.length > 0) {
        const footnotesByPage = {};
        this.footnotes.forEach(fn => {
          const page = fn.page || 1;
          if (!footnotesByPage[page]) footnotesByPage[page] = [];
          footnotesByPage[page].push(fn);
        });

        let footnotesHtml = '<div class="preview-footnotes">';
        Object.keys(footnotesByPage).sort((a, b) => a - b).forEach(page => {
          footnotesHtml += `<div class="footnote-page-group">`;
          footnotesHtml += `<span class="footnote-page-label">p.${page}</span>`;
          footnotesByPage[page].forEach(fn => {
            footnotesHtml += `<div class="footnote-entry"><sup>${fn.superscript})</sup> ${fn.content}</div>`;
          });
          footnotesHtml += `</div>`;
        });
        footnotesHtml += "</div>";
        htmlContent += footnotesHtml;
      }

      this.previewContent = htmlContent;
      this.showPreview = true;
    },

    async getData() {
      return { text: this.content, comments: this.footnotes };
    },

    async getHtml() {
      return this.textToHtml(this.content);
    },
  },
  watch: {
    value(newValue) {
      // 내부 편집 중이면 무시 (커서 유지)
      if (this.isInternalEdit) return;
      const textValue = this.htmlToText(newValue || "");
      if (textValue !== this.content) {
        this.content = textValue;
      }
    },
    comments: {
      handler(newComments) {
        if (newComments && Array.isArray(newComments)) {
          this.footnotes = [...newComments];
        }
      },
      immediate: true,
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.paper-editor {
  font-family: "Noto Sans KR", sans-serif;
}

/* 툴바 */
.editor-toolbar {
  display: flex;
  gap: 8px;
  padding: 12px;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-bottom: none;
}

.toolbar-spacer {
  flex: 1;
}

.toolbar-btn {
  padding: 8px 14px;
  border: 1px solid #ccc;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
    border-color: #999;
  }

  &.primary {
    background: #f97316;
    color: #fff;
    border-color: #f97316;

    &:hover {
      background: #ea580c;
    }
  }

  &.format-btn {
    padding: 8px 12px;
    min-width: 36px;
    font-size: 14px;
    font-family: serif;

    strong {
      font-weight: 700;
    }

    em {
      font-style: italic;
    }
  }
}

.toolbar-divider {
  width: 1px;
  background: #ddd;
  margin: 0 4px;
}

/* 텍스트 입력 */
.text-input {
  width: 100%;
  padding: 16px;
  font-family: "D2Coding", monospace;
  font-size: 13px;
  line-height: 1.6;
  border: 1px solid #e0e0e0;
  resize: none;
  outline: none;

  &:focus {
    border-color: #f97316;
  }
}

/* 모달 공통 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-content {
  background: #fff;
  border-radius: 12px;
  max-width: 700px;
  max-height: 80vh;
  overflow: auto;
  width: 90%;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;

  h3 {
    margin: 0;
    font-size: 16px;
  }
}

.close-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;

  &:hover {
    color: #333;
  }
}

.modal-body {
  padding: 20px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 20px;
  border-top: 1px solid #e0e0e0;
  background: #f8f9fa;
}

/* 사용법 모달 */
.help-content {
  h4 {
    font-size: 14px;
    margin: 16px 0 8px;
    color: #2563eb;

    &:first-child {
      margin-top: 0;
    }
  }

  pre {
    background: #f3f4f6;
    padding: 12px;
    border-radius: 6px;
    font-size: 12px;
    overflow-x: auto;
  }
}

/* 주석 모달 */
.footnote-modal {
  max-width: 500px;
}

.footnote-form-row {
  display: flex;
  gap: 16px;
  margin-bottom: 12px;
}

.footnote-field {
  margin-bottom: 12px;

  label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 6px;
  }

  input, textarea {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 13px;
  }

  textarea {
    resize: vertical;
  }
}

/* 표 삽입 모달 */
.table-modal {
  max-width: 600px;
}

/* 그림 삽입 모달 */
.figure-modal {
  max-width: 550px;
}

.figure-form-row {
  display: flex;
  gap: 16px;
  margin-bottom: 12px;
}

.figure-field {
  margin-bottom: 12px;

  label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 6px;
  }

  input[type="number"] {
    width: 70px;
    padding: 8px 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
  }

  input[type="text"] {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
  }

  input[type="file"] {
    font-size: 13px;
  }
}

.figure-preview {
  margin-top: 16px;

  label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 6px;
  }
}

.figure-preview-img {
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
  text-align: center;

  img {
    max-width: 100%;
    max-height: 200px;
    border-radius: 4px;
  }
}

.table-hint {
  font-size: 13px;
  color: #666;
  margin-bottom: 16px;
}

.table-form-row {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.table-field {
  margin-bottom: 12px;

  label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 6px;
  }

  input[type="number"] {
    width: 70px;
    padding: 8px 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
  }

  input[type="text"] {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
  }
}

.table-data-input {
  width: 100%;
  height: 120px;
  padding: 12px;
  font-family: monospace;
  font-size: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  resize: vertical;
}

.table-preview {
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
  min-height: 60px;
  overflow-x: auto;

  .placeholder {
    color: #999;
    font-size: 12px;
  }

  .preview-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;

    th, td {
      padding: 6px 8px;
      border: 1px solid #ddd;
      text-align: left;
    }

    th {
      background: #f5f5f5;
      font-weight: 600;
    }
  }
}

/* 주석 목록 */
.footnote-list-top {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #fff;
  overflow: hidden;
  margin-bottom: 0;
}

.footnote-list-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
}

.footnote-list-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.footnote-count {
  font-size: 13px;
  color: #666;
}

.add-btn {
  margin-left: auto;
  padding: 4px 12px !important;
  font-size: 13px !important;
}

.footnote-list-content {
  padding: 12px 16px;
  max-height: 200px;
  overflow-y: auto;
}

.footnote-item {
  padding: 8px 0;

  &:not(:last-child) {
    border-bottom: 1px solid #f0f0f0;
    padding-bottom: 12px;
    margin-bottom: 8px;
  }
}

.footnote-item-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.footnote-number {
  color: #2563eb;
  font-weight: bold;
  font-size: 14px;
}

.footnote-page {
  font-size: 11px;
  color: #666;
  background: #f0f0f0;
  padding: 2px 8px;
  border-radius: 4px;
}

.footnote-delete {
  margin-left: auto;
  background: none;
  border: none;
  color: #bbb;
  cursor: pointer;
  font-size: 16px;

  &:hover {
    color: #e53935;
  }
}

.footnote-text {
  font-size: 13px;
  color: #333;
  padding-left: 24px;
}

/* 미리보기 콘텐츠 */
.preview-modal {
  max-width: 800px;
}

.preview-content {
  font-family: "Noto Serif KR", serif;
  font-size: 11px;
  line-height: 1.6;

  :deep(strong) {
    font-weight: 700;
  }

  :deep(em) {
    font-style: italic;
  }

  :deep(p) {
    margin: 6px 0;
    text-align: justify;
    text-indent: 1em;
  }

  :deep(h2) {
    font-size: 13px;
    font-weight: 700;
    margin: 20px 0 10px;
    padding-bottom: 4px;
    border-bottom: 1.5px solid #333;
  }

  :deep(h3) {
    font-size: 12px;
    font-weight: 600;
    margin: 16px 0 8px;
  }

  :deep(h4), :deep(h5) {
    font-size: 11px;
    font-weight: 600;
    margin: 12px 0 6px;
  }

  :deep(.blockquote) {
    margin: 12px 0;
    padding: 10px 14px;
    background: #f9fafb;
    border-left: 3px solid #9ca3af;
    font-size: 10px;
  }

  :deep(.table-container) {
    margin: 14px 0;
  }

  :deep(.table-basic) {
    width: 100%;
    border-collapse: collapse;
    border: 1px solid #ddd;
    font-size: 9px;

    caption {
      padding: 8px;
      text-align: center;
      font-size: 10px;
      font-weight: 600;
    }

    th, td {
      padding: 6px 8px;
      border: 1px solid #ddd;
    }

    th {
      background: #f5f5f5;
      font-weight: 600;
    }
  }

  :deep(.figure-container) {
    margin: 14px 0;
    text-align: center;
  }

  :deep(.figure-placeholder) {
    display: inline-flex;
    width: 80%;
    height: 80px;
    background: #f3f4f6;
    border: 1px dashed #d1d5db;
    border-radius: 4px;
    align-items: center;
    justify-content: center;
    color: #9ca3af;
    font-size: 10px;
  }

  :deep(.figure-caption) {
    margin-top: 6px;
    font-size: 10px;
    font-weight: 600;
  }

  :deep(.references-title) {
    margin-top: 24px;
    font-size: 13px;
    font-weight: 700;
    margin-bottom: 12px;
  }

  :deep(.reference-item) {
    margin: 5px 0;
    padding-left: 1.5em;
    text-indent: -1.5em;
    font-size: 9px;
  }

  :deep(.preview-footnotes) {
    margin-top: 40px;
    padding-top: 16px;
    border-top: 1px solid #000;
    width: 40%;

    .footnote-page-group {
      margin-bottom: 10px;
    }

    .footnote-page-label {
      font-size: 9px;
      color: #666;
      margin-bottom: 4px;
      display: block;
    }

    .footnote-entry {
      font-size: 9px;
      margin: 4px 0;
      line-height: 1.4;
    }
  }
}
</style>
